/**
 * Questo file contiene le funzioni di validazione dei campi dei form.
 * Per esempio per validare il codice fiscale si controlla la lunghezza:
 * se non è della lunghezza giusta fa la reject con la promise e non fa fare il submit della form con il messaggio di errore
 * sotto, altrimenti lascia andare normalmente.
 * Se vuoi creare una nuova validazione crei la funzioni e aggiungi la funzione nell'export finale
 */

import { isDate } from "lodash";
import GeneralRepository from "core/data/GeneralRepository";
import ValidatorsRepository from "core/data/ValidatorsRepository";
import { checkAssistedExists, checkLoginExists, checkPersRifExists, monthCF } from "./Helper";
import i18n from "./i18n";
import { RuleObject } from "antd/lib/form";
import { IValidateCfVsFormData } from "core/interfaces/util/IValidators";

// eslint-disable-next-line
const EMAIL_REGEX = new RegExp(
	'^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$'
);
const PHONE_REGEX = new RegExp(
	"^([+]39)?(32[^15\\D]|33[^20\\D]|34[0-9]|35[^2-46-9\\D]|36[^45790\\D]|37[^6\\D]|38[^125-7\\D]|39[^4-689\\D])(\\d){6,7}$"
);

const FISCAL_CODE_REGEX = new RegExp("^[A-Z]{6}[0-9]{2}[A-Z]{1}[0-9]{2}[A-Z][0-9]{3}[A-Z]{1}$");

const validateCf = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (value) {
		let cfGiorno = value.substring(9, 11);
		let cfMese = value.substring(8, 9);
		let cfAnno = value.substring(6, 8);
		let municipalityCode = value.substring(11, 15);

		// il codice fiscale deve essere composto da almeno 16 caratteri
		if (!FISCAL_CODE_REGEX.test(value)) {
			await Promise.reject(text);
		}
		// le posizioni corrispondenti la data di nascita non sono valide
		if (isNaN(Number(cfGiorno)) || isNaN(Number(cfAnno)) || monthCF[cfMese] === undefined) {
			await Promise.reject(text);
		}

		let sesso = Number(cfGiorno) > 40 ? "F" : "M";

		let giornoNascita = Number(cfGiorno);

		if (sesso === "F") {
			giornoNascita = Number(cfGiorno) - 40;
		}

		// i giorni inseriti non sono validi
		if (new Date(Number(cfAnno), monthCF[cfMese], giornoNascita).getMonth() !== monthCF[cfMese]) {
			await Promise.reject(text);
		}

		let comune = await GeneralRepository.getComuniByCf({
			CfComune: municipalityCode,
			OnlyValid: false,
			culture: i18n.language,
		});
		// il codice del comune non esiste
		if (!comune) {
			await Promise.reject(text);
		}
	}

	await Promise.resolve(value);
};

const validateExixtsAssistedCf = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (value) {
		if (value.length === 16 && (await checkAssistedExists(value))) {
			await Promise.reject(text);
		}
	}

	await Promise.resolve(value);
};

const validateNotExixtsLogInCf = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (value) {
		if (value.length === 16 && (await checkLoginExists(value))) {
			await Promise.reject(text);
		}
	}

	await Promise.resolve(value);
};

const validateNotExixtsPersRifCf = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (value) {
		if (value.length === 16) {
			let loginExists = await checkLoginExists(value);
			let persRifExists = await checkPersRifExists(value);
			if (!loginExists && persRifExists) await Promise.reject(text);
		}
	}

	await Promise.resolve(value);
};

const validateExixtsLogInCf = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (value) {
		if (value.length === 16 && (await checkLoginExists(value))) {
			await Promise.reject(text);
		}
	}

	await Promise.resolve(value);
};

const validateExixtsPersRifCf = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (value) {
		if (value.length === 16) {
			let loginExists = await checkLoginExists(value);
			let persRifExists = await checkPersRifExists(value);
			if (!loginExists && !persRifExists) await Promise.reject(text);
		}
	}

	await Promise.resolve(value);
};

const validateCfVsFormData = async (rule: RuleObject, value: any, callback: any, text: string, form: IValidateCfVsFormData) => {
	// controllo che il codice fiscale sia di 16 caratteri
	if (value && value.length === 16) {
		// controllo che il codice fiscale abbia una struttura valida
		if (FISCAL_CODE_REGEX.test(value)) {
			if (form && form.Nome && form.Cognome && form.ComuneNascita && form.IstatComuneNascita && form.DataNascita && form.Sesso) {
				// verifica che per i dati forniti il codice fiscale sia valido
				let fiscalCodeValid = await GeneralRepository.checkFiscalCodeValidity({
					FormData: form,
					FiscalCode: value,
					culture: i18n.language,
				});

				if (!fiscalCodeValid) {
					await Promise.reject(text);
				}
			}
		}
	}

	await Promise.resolve(value);
};

const validateDate = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (value) {
		if (!isDate(value)) {
			await Promise.reject(text);
		}
	}

	await Promise.resolve(value);
};

const validatePhoneNumber = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (!validatePhoneNumberFormat(value)) {
		await Promise.reject(text);
	}

	await Promise.resolve(value);
};

const validatePhoneNumberFormat = (value: any) => {
	if (!PHONE_REGEX.test(value)) {
		return false;
	}

	return true;
};

const validateEmail = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (!EMAIL_REGEX.test(value)) {
		await Promise.reject(text);
	}

	await Promise.resolve(value);
};

const validateEmailLogin = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (!EMAIL_REGEX.test(value)) {
		await Promise.reject(text);
	} else {
		var esitoCheck = await ValidatorsRepository.checkMail({ Value: value, culture: i18n.language });
		if (!esitoCheck?.IsValid) await Promise.reject(esitoCheck?.ErrorMessage);
	}

	await Promise.resolve(value);
};

const validateExistsEmailLogin = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (EMAIL_REGEX.test(value)) {
		var esitoCheck = await ValidatorsRepository.checkMail({ Value: value, culture: i18n.language });
		if (esitoCheck && esitoCheck?.IsValid) await Promise.reject(text);
	}

	await Promise.resolve(value);
};

const validatePassword = async (rule: RuleObject, value: any, callback: any, text: string) => {
	let minLenght = await GeneralRepository.passwordMinLenghtGet();
	// eslint-disable-next-line
	const PASS_REGEX = new RegExp(`(?=^.{${minLenght},}$)(?=.*\\d)((?=.*\\W+)|(?=.*[_]+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$`);

	if (!PASS_REGEX.test(value)) {
		await Promise.reject(text);
	}

	await Promise.resolve(value);
};

const validateSwitch = async (rule: RuleObject, value: any, callback: any, text: string) => {
	if (!value) {
		await Promise.reject(text);
	}

	await Promise.resolve(text);
};

export {
	validateCf,
	validatePhoneNumber,
	validateEmail,
	validatePassword,
	validateDate,
	validateEmailLogin,
	validateExixtsAssistedCf,
	validateNotExixtsLogInCf,
	validateNotExixtsPersRifCf,
	validateExixtsLogInCf,
	validateExixtsPersRifCf,
	validateExistsEmailLogin,
	validateSwitch,
	validatePhoneNumberFormat,
	validateCfVsFormData,
};
